import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, Newspaper } from "lucide-react";
import { useNewsSources } from "@/hooks/useContentStudio";
import { ArticleDetailModal } from "@/components/news/ArticleDetailModal";
import type { NewsSource } from "@/types/content-studio";
import type { NewsIntelligenceRow } from "@/hooks/useNewsIntelligence";

interface NewsSourceTabProps {
  selected: NewsSource[];
  onAdd: (source: NewsSource) => void;
  onRemove: (id: string) => void;
}

/** Map a ContentStudio NewsSource to the shape ArticleDetailModal expects. */
function toModalRow(s: NewsSource): NewsIntelligenceRow {
  return {
    id: s.id,
    title: s.title,
    url: null,
    published_at: s.published_at,
    source_feed: s.source_feed,
    summary: s.summary,
    short_summary: s.short_summary,
    executive_briefing: s.executive_briefing,
    opportunity_analysis: s.opportunity_analysis,
    match_type: null,
    relevance_score: null,
    priority_level: null,
    priority_label: null,
    signal_type: s.signal_type,
    is_competitor_alert: s.is_competitor_alert ?? false,
    is_prospect_opportunity: s.is_prospect_opportunity ?? false,
    is_investor_activity: false,
    matched_keywords: s.matched_keywords,
    prospect_company_name: s.prospect_company_name,
    prospect_confidence: null,
    linkedin_score: s.linkedin_score,
    linkedin_post_draft: s.linkedin_post_draft,
    is_primary: false,
    also_covered_by: null,
    is_tier2: false,
    email_sent: false,
    email_sent_at: null,
    data_source: "news_intelligence",
    created_at: null,
    updated_at: null,
    source_article_id: null,
    source_intelligence_id: null,
    action_items: null,
    outreach_guidance: s.outreach_guidance,
    match_reasoning: s.match_reasoning,
    matched_competitors: null,
    matched_accounts: null,
    matched_categories: null,
    suggested_contacts: null,
    enriched_content: s.enriched_content,
    market_context: s.market_context,
    companies_mentioned: s.companies_mentioned,
    enrichment_metadata: s.enrichment_metadata,
    related_articles: null,
    enriched_at: s.enriched_at,
    analyzed_at: null,
    company_id: null,
    company_name: null,
    author: s.author ?? null,
    article_categories: null,
  } as unknown as NewsIntelligenceRow;
}

export function NewsSourceTab({ selected, onAdd, onRemove }: NewsSourceTabProps) {
  const [search, setSearch] = useState("");
  const [modalArticle, setModalArticle] = useState<NewsIntelligenceRow | null>(null);
  const { data: news, isLoading } = useNewsSources(50);

  const filtered = (news || []).filter((n) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      n.title?.toLowerCase().includes(q) ||
      n.summary?.toLowerCase().includes(q) ||
      n.signal_type?.toLowerCase().includes(q)
    );
  });

  const selectedIds = new Set(selected.map((s) => s.id));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search news articles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-8 text-xs"
        />
      </div>

      <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
        {filtered.map((article) => {
          const isSelected = selectedIds.has(article.id);
          return (
            <Card
              key={article.id}
              className={`cursor-pointer transition-colors ${isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
              onClick={() => setModalArticle(toModalRow(article))}
            >
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground line-clamp-2">{article.title}</p>
                    <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">
                      {article.short_summary || article.summary}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      {article.signal_type && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0">{article.signal_type}</Badge>
                      )}
                      {article.published_at && (
                        <span className="text-[9px] text-muted-foreground">
                          {new Date(article.published_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className="shrink-0 mt-0.5"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isSelected) { onRemove(article.id); } else { onAdd(article); }
                    }}
                  >
                    <Checkbox checked={isSelected} className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Newspaper className="w-6 h-6 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No articles found</p>
          </div>
        )}
      </div>

      <ArticleDetailModal
        article={modalArticle}
        open={!!modalArticle}
        onOpenChange={(open) => { if (!open) setModalArticle(null); }}
      />
    </div>
  );
}
